import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { 
  Settings, 
  Mail, 
  Users, 
  Building2, 
  Save, 
  Plus, 
  Trash2, 
  Pencil,
  Shield,
  ShieldCheck,
  X,
  Check,
  Truck
} from "lucide-react";

const API = '/api';

export default function Configuracoes() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Email configs
  const [emailsEncomendas, setEmailsEncomendas] = useState([]);
  const [emailsRelatorios, setEmailsRelatorios] = useState([]);
  const [newEmailEncomenda, setNewEmailEncomenda] = useState("");
  const [newEmailRelatorio, setNewEmailRelatorio] = useState("");
  
  // Company config
  const [empresa, setEmpresa] = useState({
    nome_empresa: "FARMAXMP",
    nif_empresa: "",
    morada_empresa: "",
    telefone_empresa: "",
    email_empresa: "",
  });
  
  // Users
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    nome: "",
    email: "",
    role: "user",
    ativo: true,
    password: "",
  });

  // Armazenistas
  const [armazenistas, setArmazenistas] = useState([]);
  const [newArmazenista, setNewArmazenista] = useState("");
  const [editingArmazenista, setEditingArmazenista] = useState(null);
  const [editArmazenistaName, setEditArmazenistaName] = useState("");

  useEffect(() => {
    if (isAdmin()) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [emailsRes, empresaRes, usersRes, armazenistasRes] = await Promise.all([
        axios.get(`${API}/config/emails`),
        axios.get(`${API}/config/empresa`),
        axios.get(`${API}/users`),
        axios.get(`${API}/armazenistas/all`),
      ]);
      
      setEmailsEncomendas(emailsRes.data.emails_encomendas || []);
      setEmailsRelatorios(emailsRes.data.emails_relatorios || []);
      setEmpresa(empresaRes.data);
      setUsers(usersRes.data);
      setArmazenistas(armazenistasRes.data || []);
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveEmails = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/config/emails`, {
        emails_encomendas: emailsEncomendas,
        emails_relatorios: emailsRelatorios,
      });
      toast.success("Configurações de email guardadas");
    } catch (error) {
      toast.error("Erro ao guardar emails");
    } finally {
      setSaving(false);
    }
  };

  const saveEmpresa = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/config/empresa`, empresa);
      toast.success("Dados da empresa guardados");
    } catch (error) {
      toast.error("Erro ao guardar dados da empresa");
    } finally {
      setSaving(false);
    }
  };

  const addEmail = (type) => {
    const email = type === "encomendas" ? newEmailEncomenda : newEmailRelatorio;
    if (!email || !email.includes("@")) {
      toast.error("Email inválido");
      return;
    }
    
    if (type === "encomendas") {
      if (emailsEncomendas.includes(email)) {
        toast.error("Email já existe na lista");
        return;
      }
      setEmailsEncomendas([...emailsEncomendas, email]);
      setNewEmailEncomenda("");
    } else {
      if (emailsRelatorios.includes(email)) {
        toast.error("Email já existe na lista");
        return;
      }
      setEmailsRelatorios([...emailsRelatorios, email]);
      setNewEmailRelatorio("");
    }
  };

  const removeEmail = (type, email) => {
    if (type === "encomendas") {
      setEmailsEncomendas(emailsEncomendas.filter(e => e !== email));
    } else {
      setEmailsRelatorios(emailsRelatorios.filter(e => e !== email));
    }
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setUserForm({
      nome: u.nome,
      email: u.email,
      role: u.role,
      ativo: u.ativo,
      password: "",
    });
    setUserDialogOpen(true);
  };

  const saveUser = async () => {
    try {
      if (editingUser) {
        // Atualizar utilizador existente
        const data = {
          nome: userForm.nome,
          email: userForm.email,
          role: userForm.role,
          ativo: userForm.ativo,
        };
        if (userForm.password) {
          data.password = userForm.password;
        }
        
        await axios.put(`${API}/users/${editingUser.id}`, data);
        toast.success("Utilizador atualizado");
      } else {
        // Criar novo utilizador
        if (!userForm.password) {
          toast.error("Password é obrigatória para novo utilizador");
          return;
        }
        await axios.post(`${API}/auth/register`, {
          nome: userForm.nome,
          email: userForm.email,
          password: userForm.password,
          role: userForm.role,
        });
        toast.success("Utilizador criado com sucesso");
      }
      setUserDialogOpen(false);
      setEditingUser(null);
      setUserForm({ nome: "", email: "", role: "user", ativo: true, password: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar utilizador");
    }
  };

  const openNewUserDialog = () => {
    setEditingUser(null);
    setUserForm({ nome: "", email: "", role: "user", ativo: true, password: "" });
    setUserDialogOpen(true);
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Utilizador eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao eliminar utilizador");
    }
  };

  // Armazenistas functions
  const addArmazenista = async () => {
    if (!newArmazenista.trim()) {
      toast.error("Nome do armazenista é obrigatório");
      return;
    }
    try {
      await axios.post(`${API}/armazenistas`, { nome: newArmazenista.trim() });
      toast.success("Armazenista adicionado");
      setNewArmazenista("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao adicionar armazenista");
    }
  };

  const updateArmazenista = async (id) => {
    if (!editArmazenistaName.trim()) {
      toast.error("Nome do armazenista é obrigatório");
      return;
    }
    try {
      await axios.put(`${API}/armazenistas/${id}`, { nome: editArmazenistaName.trim() });
      toast.success("Armazenista atualizado");
      setEditingArmazenista(null);
      setEditArmazenistaName("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar armazenista");
    }
  };

  const deleteArmazenista = async (id) => {
    try {
      await axios.delete(`${API}/armazenistas/${id}`);
      toast.success("Armazenista eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao eliminar armazenista");
    }
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="configuracoes-page">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="font-heading font-semibold text-lg text-slate-700 mb-2">
              Acesso Restrito
            </h2>
            <p className="text-slate-500">
              Apenas administradores podem aceder às configurações do sistema.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="configuracoes-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-extrabold text-3xl text-slate-900">
          Configurações
        </h1>
        <p className="text-slate-500 mt-1">
          Gestão do sistema e permissões
        </p>
      </div>

      <Tabs defaultValue="emails" className="space-y-6">
        <TabsList className="bg-white border">
          <TabsTrigger value="emails" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
            <Mail className="w-4 h-4 mr-2" />
            Emails
          </TabsTrigger>
          <TabsTrigger value="armazenistas" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
            <Truck className="w-4 h-4 mr-2" />
            Armazenistas
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
            <Users className="w-4 h-4 mr-2" />
            Utilizadores
          </TabsTrigger>
          <TabsTrigger value="empresa" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
            <Building2 className="w-4 h-4 mr-2" />
            Empresa
          </TabsTrigger>
        </TabsList>

        {/* Tab Emails */}
        <TabsContent value="emails" className="space-y-6">
          {/* Emails Encomendas */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Emails para Encomendas
              </CardTitle>
              <CardDescription>
                Emails que recebem as notas de encomenda automaticamente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {emailsEncomendas.map((email) => (
                  <Badge key={email} variant="secondary" className="px-3 py-1.5 text-sm gap-2">
                    {email}
                    <button
                      onClick={() => removeEmail("encomendas", email)}
                      className="hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                {emailsEncomendas.length === 0 && (
                  <p className="text-slate-400 text-sm">Nenhum email configurado</p>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="novo@email.com"
                  value={newEmailEncomenda}
                  onChange={(e) => setNewEmailEncomenda(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addEmail("encomendas")}
                  className="max-w-xs"
                />
                <Button
                  variant="outline"
                  onClick={() => addEmail("encomendas")}
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Emails Relatórios */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-orange-600" />
                Emails para Relatórios
              </CardTitle>
              <CardDescription>
                Emails que recebem os relatórios mensais
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {emailsRelatorios.map((email) => (
                  <Badge key={email} variant="secondary" className="px-3 py-1.5 text-sm gap-2">
                    {email}
                    <button
                      onClick={() => removeEmail("relatorios", email)}
                      className="hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                {emailsRelatorios.length === 0 && (
                  <p className="text-slate-400 text-sm">Nenhum email configurado</p>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="novo@email.com"
                  value={newEmailRelatorio}
                  onChange={(e) => setNewEmailRelatorio(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addEmail("relatorios")}
                  className="max-w-xs"
                />
                <Button
                  variant="outline"
                  onClick={() => addEmail("relatorios")}
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar
                </Button>
              </div>
            </CardContent>
          </Card>

          <Button 
            onClick={saveEmails} 
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? "A guardar..." : "Guardar Emails"}
          </Button>
        </TabsContent>

        {/* Tab Armazenistas */}
        <TabsContent value="armazenistas">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-600" />
                Gestão de Armazenistas ({armazenistas.length})
              </CardTitle>
              <CardDescription>
                Adicionar, editar ou remover armazenistas (distribuidores)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Adicionar novo */}
              <div className="flex gap-2">
                <Input
                  placeholder="Nome do novo armazenista"
                  value={newArmazenista}
                  onChange={(e) => setNewArmazenista(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addArmazenista()}
                  className="max-w-md"
                />
                <Button
                  onClick={addArmazenista}
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar
                </Button>
              </div>

              {/* Lista */}
              <div className="border rounded-lg divide-y">
                {armazenistas.map((arm) => (
                  <div key={arm.id} className="p-3 flex items-center justify-between hover:bg-slate-50">
                    {editingArmazenista === arm.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          value={editArmazenistaName}
                          onChange={(e) => setEditArmazenistaName(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && updateArmazenista(arm.id)}
                          className="max-w-xs"
                          autoFocus
                        />
                        <Button
                          size="sm"
                          onClick={() => updateArmazenista(arm.id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingArmazenista(null);
                            setEditArmazenistaName("");
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3">
                          <Truck className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{arm.nome}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingArmazenista(arm.id);
                              setEditArmazenistaName(arm.nome);
                            }}
                            className="text-slate-400 hover:text-blue-600"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-slate-400 hover:text-red-500"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Eliminar Armazenista?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Esta ação não pode ser revertida. O armazenista "{arm.nome}" será permanentemente eliminado.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteArmazenista(arm.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {armazenistas.length === 0 && (
                  <div className="p-8 text-center text-slate-400">
                    <Truck className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>Nenhum armazenista configurado</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Utilizadores */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Gestão de Utilizadores ({users.length})
                  </CardTitle>
                  <CardDescription>
                    Gerir permissões e acessos dos utilizadores
                  </CardDescription>
                </div>
                <Button onClick={openNewUserDialog} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Utilizador
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    <TableHead className="font-bold">Nome</TableHead>
                    <TableHead className="font-bold">Email</TableHead>
                    <TableHead className="font-bold">Permissão</TableHead>
                    <TableHead className="font-bold">Estado</TableHead>
                    <TableHead className="font-bold text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {u.role === "admin" ? (
                            <ShieldCheck className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Shield className="w-4 h-4 text-slate-400" />
                          )}
                          <span className="font-medium">{u.nome}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{u.email}</TableCell>
                      <TableCell>
                        <Badge 
                          variant={u.role === "admin" ? "default" : "secondary"}
                          className={u.role === "admin" ? "bg-blue-600" : ""}
                        >
                          {u.role === "admin" ? "Administrador" : "Utilizador"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {u.ativo ? (
                          <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                            <Check className="w-3 h-3 mr-1" />
                            Ativo
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50">
                            <X className="w-3 h-3 mr-1" />
                            Inativo
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditUser(u)}
                            className="text-slate-400 hover:text-blue-600"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {u.id !== user?.id && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-slate-400 hover:text-red-500"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Eliminar Utilizador?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Esta ação não pode ser revertida. O utilizador "{u.nome}" será permanentemente eliminado.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteUser(u.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Eliminar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Dialog Editar Utilizador */}
          <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingUser ? "Editar Utilizador" : "Novo Utilizador"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={userForm.nome}
                    onChange={(e) => setUserForm({ ...userForm, nome: e.target.value })}
                    placeholder="Nome completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    placeholder="email@exemplo.pt"
                    disabled={!!editingUser}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Permissão *</Label>
                  <Select
                    value={userForm.role}
                    onValueChange={(v) => setUserForm({ ...userForm, role: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Utilizador</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editingUser && (
                  <div className="flex items-center justify-between">
                    <Label>Conta Ativa</Label>
                    <Switch
                      checked={userForm.ativo}
                      onCheckedChange={(v) => setUserForm({ ...userForm, ativo: v })}
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>
                    {editingUser ? "Nova Password (deixar vazio para manter)" : "Password *"}
                  </Label>
                  <Input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    placeholder="••••••••"
                    required={!editingUser}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setUserDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={saveUser} className="bg-blue-600 hover:bg-blue-700">
                    {editingUser ? "Guardar" : "Criar Utilizador"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Tab Empresa */}
        <TabsContent value="empresa">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Dados da Empresa
              </CardTitle>
              <CardDescription>
                Informações que aparecem nas notas de encomenda e relatórios
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome da Empresa</Label>
                  <Input
                    value={empresa.nome_empresa}
                    onChange={(e) => setEmpresa({ ...empresa, nome_empresa: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>NIF</Label>
                  <Input
                    value={empresa.nif_empresa}
                    onChange={(e) => setEmpresa({ ...empresa, nif_empresa: e.target.value })}
                    placeholder="Ex: 507571835"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Morada</Label>
                  <Input
                    value={empresa.morada_empresa}
                    onChange={(e) => setEmpresa({ ...empresa, morada_empresa: e.target.value })}
                    placeholder="Ex: Rua Principal, 123, 4000-000 Porto"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={empresa.telefone_empresa}
                    onChange={(e) => setEmpresa({ ...empresa, telefone_empresa: e.target.value })}
                    placeholder="Ex: 220 123 456"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={empresa.email_empresa}
                    onChange={(e) => setEmpresa({ ...empresa, email_empresa: e.target.value })}
                    placeholder="Ex: geral@farmaxmp.pt"
                  />
                </div>
              </div>
              <Button 
                onClick={saveEmpresa} 
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 gap-2 mt-4"
              >
                <Save className="w-4 h-4" />
                {saving ? "A guardar..." : "Guardar Dados da Empresa"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
